import { PlatformBridge } from './bridge';

export interface NativeAppInfo {
  versionName: string;
  packageName: string;
  environment: string;
}

/**
 * Le suffixe du package Android est posé au build par `build.gradle`
 * (com.ciblijob / com.ciblijob.staging / com.ciblijob.dev) : c'est la source
 * de vérité de l'environnement de l'APK installé.
 */
function environmentFromPackage(packageName: string): string {
  if (packageName.endsWith('.staging')) return 'STAGING';
  if (packageName.endsWith('.dev')) return 'DEV';
  return 'PRODUCTION';
}

/**
 * Côté web, l'environnement se lit sur le domaine qui sert le bundle.
 * Matching exact sur les hôtes configurés (packages/config/config.json) :
 * jamais de sous-chaîne, un domaine ressemblant ne doit pas être reconnu.
 */
export function environmentFromHostname(hostname: string): string {
  const host = hostname.toLowerCase();
  if (host === 'cibli.agency.lonestone.io') return 'STAGING';
  if (host === 'app.ciblijob.fr') return 'PRODUCTION';
  if (host === 'localhost') return 'DEV';
  // Vite allowlist : 192.168.1.2 → 192.168.1.254 (octet final sans zéro initial).
  const parts = host.split('.');
  if (
    parts.length === 4 &&
    parts[0] === '192' &&
    parts[1] === '168' &&
    parts[2] === '1' &&
    parts[3] === String(Number(parts[3])) &&
    Number(parts[3]) >= 2 &&
    Number(parts[3]) <= 254
  ) {
    return 'DEV';
  }
  return 'NON RECONNU';
}

export const AppInfoBridge = {
  /**
   * Métadonnées de l'APK installé, ou null en navigateur web / sur un APK
   * construit avant l'ajout de `getAppInfo` (appels gracieusement ignorés).
   */
  async getNativeAppInfo(): Promise<NativeAppInfo | null> {
    if (!PlatformBridge.isCapacitor) return null;

    const plugin = PlatformBridge.capacitorPlugins?.PackageInfo as
      { getAppInfo?: () => Promise<{ versionName?: string; packageName?: string }> } | undefined;
    if (!plugin?.getAppInfo) return null;

    try {
      const info = await plugin.getAppInfo();
      const packageName = info.packageName ?? '';
      if (!packageName) return null;
      return {
        versionName: info.versionName ?? '',
        packageName,
        environment: environmentFromPackage(packageName),
      };
    } catch {
      return null;
    }
  },
};
